import { FiltersChips } from 'common/components/Filters/FiltersChips';
import { useTranslation } from 'react-i18next';
import { SESSION_STATUS, SimInventoryFilters } from 'simInventory/models/SimProfile.model';

import { useAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import {
  accountsFilterAtom,
  customFieldsFilterAtom,
  mobileNetworkOperatorFilterAtom,
  mnoNamesFilterAtom,
  productCodesFilterAtom,
  SIM_PRODUCTS_FILTERS_INITIAL_VALUES,
  simProdcutsSubscriptionStatusFilterAtom,
  simProductsFiltersAtom,
  tagsFilterAtom,
  simProductsMobileNetworkOperatorIdFilterAtom,
} from 'atoms/filters/sim-products';
import { TagsChips } from 'tags/Components/TagsChips';
import { AccountChips } from 'common/components/FilterChip/AccountChips';
import { MnosChips } from 'common/components/FilterChip/MnosChips';
import { ProductCodesChips } from 'common/components/FilterChip/ProductCodesChips';
import { CustomFieldsChips } from 'common/components/FilterChip/CustomFields/CustomFieldsChips';
import { SubscriptionStatusChips } from 'src/common/components/FilterChip/SubscriptionStatusChips';
import { createChipsContentText } from 'src/common/components/Chips/utils/chipsContentUtils';
import { omit } from 'lodash';
import { isKeyOfObject } from 'src/common/utils/isKeyOfObject';

export const SimProductsFilterChips = () => {
  const { t } = useTranslation();

  const [filters, setFilters] = useAtom(simProductsFiltersAtom);

  const otherFilters = omit(filters, [
    'tags',
    'mobileNetworkOperator',
    'accounts',
    'customFields',
    'mnoStatus',
    'productCodes',
    'subscriptionStatus',
    'mobileNetworkOperatorId',
  ]);

  const clearAll = useResetAtom(simProductsFiltersAtom);

  const onFilterClear = (filterName: string | 'iccidRange') => {
    if (filterName === 'iccidRange') {
      setFilters((currentFilters) => ({
        ...currentFilters,
        iccidFrom: '',
        iccidTo: '',
      }));

      return;
    }

    if (isKeyOfObject(filterName, SIM_PRODUCTS_FILTERS_INITIAL_VALUES)) {
      setFilters((currentFilters) => ({
        ...currentFilters,
        [filterName]: SIM_PRODUCTS_FILTERS_INITIAL_VALUES[filterName],
      }));
    }
  };

  const createValuesForChips = (
    filters: Omit<
      SimInventoryFilters,
      'tags' | 'mobileNetworkOperator' | 'accounts' | 'customFields'
    >,
  ) => {
    const filtersNames = {
      sessionStatus: t('simInventory.sessionStatus'),
      accounts: t('common.account'),
      mobileNetworkOperator: t('simInventory.mobileNetworkOperator'),
      orderNumber: t('common.orderNumber'),
      connectionId: t('common.connectionId'),
      tags: 'tags',
      iccidRange: '',
    };

    const { sessionStatus, iccidFrom, iccidTo, ...othersFilters } = filters;
    const chipsValues: Partial<Record<keyof typeof filtersNames, string>> = {};

    if (sessionStatus !== SESSION_STATUS.UNKNOWN) {
      chipsValues.sessionStatus = createChipsContentText(
        filtersNames.sessionStatus,
        sessionStatus === SESSION_STATUS.IN_SESSION
          ? t('common.inSession')
          : t('common.outSession'),
      );
    }

    if (iccidFrom !== '' || iccidTo !== '') {
      chipsValues.iccidRange = `ICCID`;
      if (iccidFrom) {
        chipsValues.iccidRange += ` ${t('common.from')}: ${iccidFrom}`;
      }

      if (iccidTo) {
        chipsValues.iccidRange += ` ${t('common.to')}: ${iccidTo}`;
      }
    }

    Object.entries(othersFilters).forEach(([key, value]) => {
      if (key === 'chosenIdType' || key === 'searchText' || key === 'mnoNames') {
        return;
      }

      if (value !== '') {
        chipsValues[key as keyof typeof filtersNames] = createChipsContentText(
          filtersNames[key as keyof typeof filtersNames],
          value,
        );
      }
    });

    return chipsValues;
  };

  const filtersChips = createValuesForChips(otherFilters);

  return (
    <FiltersChips
      externalChips={
        <>
          <TagsChips tagsAtom={tagsFilterAtom} />
          <AccountChips accountAtom={accountsFilterAtom} />
          <SubscriptionStatusChips
            subscriptionStatusAtom={simProdcutsSubscriptionStatusFilterAtom}
          />
          <ProductCodesChips productCodesAtom={productCodesFilterAtom} />
          <MnosChips
            mnoAtom={mobileNetworkOperatorFilterAtom}
            mnoNamesFilterAtom={mnoNamesFilterAtom}
            mnoIdFilterAtom={simProductsMobileNetworkOperatorIdFilterAtom}
          />
          <CustomFieldsChips customFieldsAtom={customFieldsFilterAtom} />
        </>
      }
      filters={filtersChips}
      onFilterClear={onFilterClear}
      onFiltersClear={clearAll}
    />
  );
};
